import React from 'react';
import { useSortBy, useTable, TableOptions } from 'react-table';
import { Skeletonize, Table as UiTable } from '@ui/components';
import { ArrowDropDownIcon } from '@ui/icons';
import { makeStyles } from '@ui/styles';
import { Theme } from '@ui/theme';

type Props<D extends object> = React.PropsWithChildren<{ label?: string }> &
  Omit<TableOptions<D>, 'data'> &
  Partial<Pick<TableOptions<D>, 'data'>>;

const skeletonData = Array(10).fill({});

export const Table = <D extends object>(props: Props<D>) => {
  const { children, label, data, ...rest } = props;
  const classes = useStyles({ hideOverflow: !data });
  const { getTableProps, headerGroups, prepareRow, rows, columns } = useTable(
    { data: data || skeletonData, ...rest },
    useSortBy,
  );

  return (
    <UiTable.Container className={classes.container}>
      <UiTable.Container>
        <UiTable.Table {...getTableProps()} size='small' aria-label={label}>
          <UiTable.Head className={classes.stickyHeader}>
            {children && (
              <UiTable.Row>
                <UiTable.Cell colSpan={columns.length}>
                  <div className={classes.customHeader}>{children}</div>
                </UiTable.Cell>
              </UiTable.Row>
            )}

            {headerGroups.map((headerGroup) => (
              <UiTable.Row {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <UiTable.Cell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    sortDirection={column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : false}
                  >
                    {column.render('Header')}

                    <UiTable.SortLabel
                      IconComponent={ArrowDropDownIcon}
                      active={column.isSorted}
                      direction={column.isSortedDesc ? 'desc' : 'asc'}
                    />
                  </UiTable.Cell>
                ))}
              </UiTable.Row>
            ))}
          </UiTable.Head>

          <UiTable.Body>
            {rows.map((row) => {
              prepareRow(row);

              return (
                <UiTable.Row {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <Skeletonize
                      as={UiTable.Cell}
                      SkeletonProps={{ className: classes.skeleton }}
                      {...cell.getCellProps()}
                    >
                      {cell.render('Cell')}
                    </Skeletonize>
                  ))}
                </UiTable.Row>
              );
            })}
          </UiTable.Body>
        </UiTable.Table>
      </UiTable.Container>
    </UiTable.Container>
  );
};

const useStyles = makeStyles<Theme, { hideOverflow: boolean }>((theme) => ({
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.common.white,
  },
  customHeader: {
    display: 'flex',
    minHeight: '33px',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeleton: { width: '100%' },
  container: {
    overflowY: ({ hideOverflow }) => (hideOverflow ? 'scroll' : 'auto'),
    '& > *': {
      overflowY: ({ hideOverflow }) => (hideOverflow ? 'hidden' : 'scroll'),
      height: '100%',
    },
  },
}));
